import { Facts } from "lib/models/list-models";
import { ItemModel } from "lib/services/catalogical-models";
import { ItemTaxonomyId } from "lib/taxonomy/item-taxonomy-id";

export enum SUB_TYPE {
  SUGGESTED = "SUGGESTED",
  SUPPORTED = "SUPPORTED",
}
export const calculateItemsDiff = (selectedItems: ItemModel[], initialItems?: ItemModel[]) => ({
  added: selectedItems.filter((item) => !initialItems?.some((initial) => initial.taxonomyId === item.taxonomyId)),
  removed:
    initialItems?.filter((initial) => !selectedItems.some((item) => item.taxonomyId === initial.taxonomyId)) || [],
});

const buildJobFamilyRule = (jobFamilyValue: string) => ({
  conditions: {
    all: [
      {
        fact: Facts.JOB_FAMILY,
        operator: "contains",
        value: jobFamilyValue,
      },
    ],
  },
});

export const getJobFamilyFromReason = (reason?: string): string | undefined => {
  if (!reason) return undefined;
  const match = reason.match(/contains\s+(.+)$/);
  return match ? match[1].trim() : undefined;
};

export const getJobFamilyFromRule = (rule: any): string | undefined =>
  rule?.conditions?.all?.find((c: any) => c.fact === Facts.JOB_FAMILY)?.value;

export const fetchRecommendedTools = async (
  jobFamilies: string[],
  evaluateLists: (criteria: any, options: any) => Promise<any[]>,
  allJobFamilyCategory: string,
  subType?: string[]
): Promise<ItemModel[]> => {
  const filteredJobFamilies = jobFamilies.filter((jobFamily) => jobFamily !== allJobFamilyCategory);

  if (filteredJobFamilies.length === 0) return [];

  const lists = await evaluateLists(
    { jobFamilyName: filteredJobFamilies, ...(subType && { tags: subType }) },
    { includeItemDetails: true }
  );

  const toolsMap = new Map(
    lists
      .flatMap((list) => list.fullItems)
      .filter((tool) => tool?.taxonomyId)
      .map((tool) => [tool.taxonomyId, tool])
  );
  return sortToolsByWilsonRating<ItemModel>(Array.from(toolsMap.values()));
};

export const fetchRecommendedToolsByJobFamily = async <T extends ItemModel = ItemModel>(
  jobFamilies: string[],
  evaluateLists: (criteria: any, options: any) => Promise<any[]>,
  allJobFamilyCategory: string
): Promise<Map<string, T[]>> => {
  const filteredJobFamilies = jobFamilies.filter((jobFamily) => jobFamily !== allJobFamilyCategory);

  if (filteredJobFamilies.length === 0) return new Map();

  try {
    const lists = await evaluateLists({ jobFamilyName: filteredJobFamilies }, { includeItemDetails: true, limit: 100 });

    const jobFamilyMap = new Map<string, T[]>();

    lists.forEach((list) => {
      const jobFamily =
        list.tags?.find((tag: string) => filteredJobFamilies.includes(tag)) ?? getJobFamilyFromReason(list.reason);
      if (!jobFamily) return;

      const existing = jobFamilyMap.get(jobFamily) || [];
      jobFamilyMap.set(jobFamily, [...existing, ...list.fullItems]);
    });

    return jobFamilyMap;
  } catch (error) {
    console.error("Failed to fetch recommended tools by job family:", error);
    return new Map();
  }
};

export const sortToolsByWilsonRating = <T extends ItemModel = ItemModel>(tools: T[]): T[] =>
  tools.sort((a, b) => (b.feedback?.wilsonRating ?? 0) - (a.feedback?.wilsonRating ?? 0));

export const getLinkToDetails = (item: ItemModel) => {
  const parsedTaxonomy = ItemTaxonomyId.Parse(item.taxonomyId!);
  const linkMap: Record<string, string> = {
    software: `/items/${parsedTaxonomy.getUrlSuffix()}`,
    aiforme: item.learnMoreLink,
  };
  return linkMap[parsedTaxonomy.dataDomain] || "";
};

const buildJobFamilyAndSubTypeRule = (jobFamily: string, subType: string) => ({
  conditions: {
    all: [
      {
        fact: Facts.JOB_FAMILY,
        operator: "contains",
        value: jobFamily,
      },
      {
        fact: Facts.SUB_TYPE,
        operator: "contains",
        value: subType,
      },
    ],
  },
});

export const rulesBuilder = (jobFamily: string, subType?: string) => {
  if (subType) {
    return buildJobFamilyAndSubTypeRule(jobFamily, subType);
  } else {
    return buildJobFamilyRule(jobFamily);
  }
};

export const getTypeFromTags = (tags: string[]): SUB_TYPE | undefined => {
  const subTypeValues = Object.values(SUB_TYPE) as string[];
  return tags.find((tag) => subTypeValues.includes(tag)) as SUB_TYPE | undefined;
};
